import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';

import CustomerViewerActivity from 'models/customer_viewer_activity';
import CustomerViewerActivityTarget from 'models/customer_viewer_activity_target';
import ProfileAvatar from 'components/lib/profile_avatar';
import Tooltip, { Hoverer, TooltipContext } from 'components/common/tooltip';

const MAX_AVATARS = 2;
// The elapsed label is minute-resolution, but tick faster than a minute so
// "· 1m" doesn't lag most of a minute behind the truth.
const TICK_INTERVAL_MS = 15 * 1000;

/**
 * CustomerViewersPill shows which other agents are on this customer page. One agent,
 * one entry — the container collapses an agent's sessions before we get here.
 */
export function CustomerViewersPill({ viewers }) {
  const { t } = useTranslation();
  useElapsedTick(viewers.length > 0);

  if (!viewers.length) {
    return null;
  }

  const single = viewers.length === 1;

  const pill = (
    <Pill data-aid="customerViewers-pill">
      <Avatars>
        {viewers.slice(0, MAX_AVATARS).map(viewer => (
          <AvatarSlot key={viewer.agentId}>
            <ProfileAvatar isSmall profile={{ name: viewer.name, image: viewer.image }} />
          </AvatarSlot>
        ))}
      </Avatars>
      {single ? (
        <ViewerLabel Name={Label} Status={Elapsed} t={t} viewer={viewers[0]} />
      ) : (
        <Label>{t('{{count}} Agents Viewing', { count: viewers.length })}</Label>
      )}
    </Pill>
  );

  // With one viewer the pill already shows their name and duration, so a tooltip
  // would just repeat itself.
  if (single) {
    return pill;
  }

  return (
    <ViewersTooltip margin={8} message={<ViewerList t={t} viewers={viewers} />} position="top">
      <FocusablePill label={ariaLabel(t, viewers)}>{pill}</FocusablePill>
    </ViewersTooltip>
  );
}

/**
 * Makes the collapsed pill reachable without a pointer: once several agents collapse
 * into a count, every name and duration lives in a tooltip that opens on hover only.
 *
 * A group rather than a button, because there is nothing to activate — but it needs
 * *some* role, since aria-label is not announced on the implicit generic role.
 */
function FocusablePill({ children, label }) {
  const { onBlur, onFocus } = useContext(TooltipContext);

  return (
    <FocusTarget aria-label={label} onBlur={onBlur} onFocus={onFocus} role="group" tabIndex={0}>
      {children}
    </FocusTarget>
  );
}

FocusablePill.propTypes = {
  children: PropTypes.node,
  label: PropTypes.string,
};

/**
 * An agent's name and what they're doing, as one translated phrase. Through Trans so
 * the string owns the word order while each half keeps its own styled component.
 */
function ViewerLabel({ Name, Status, t, viewer }) {
  return (
    <Trans
      components={{ name: <Name />, status: <Status /> }}
      i18nKey="<name>{{agentName}}</name><status>{{status}}</status>"
      t={t}
      values={{ agentName: viewer.name, status: describeViewer(t, viewer) }}
    />
  );
}

ViewerLabel.propTypes = {
  Name: PropTypes.elementType.isRequired,
  Status: PropTypes.elementType.isRequired,
  t: PropTypes.func.isRequired,
  viewer: PropTypes.object.isRequired,
};

/** The tooltip's content as one string, for assistive tech. */
function ariaLabel(t, viewers) {
  const viewerList = viewers
    .map(viewer => t('{{agentName}} — {{status}}', { agentName: viewer.name, status: describeViewer(t, viewer) }))
    .join(', ');

  return t('{{count}} Agents Viewing: {{viewerList}}', { count: viewers.length, viewerList });
}

CustomerViewersPill.propTypes = {
  viewers: PropTypes.arrayOf(
    PropTypes.shape({
      activity: PropTypes.string,
      activityTarget: PropTypes.string,
      agentId: PropTypes.string.isRequired,
      arrivedAt: PropTypes.string,
      image: PropTypes.string,
      name: PropTypes.string,
    })
  ).isRequired,
};

function ViewerList({ t, viewers }) {
  return (
    <ListWrapper>
      {viewers.map(viewer => (
        <ListRow key={viewer.agentId}>
          <ProfileAvatar isSmall profile={{ name: viewer.name, image: viewer.image }} />
          <ViewerLabel Name={ListName} Status={ListElapsed} t={t} viewer={viewer} />
        </ListRow>
      ))}
    </ListWrapper>
  );
}

ViewerList.propTypes = {
  t: PropTypes.func.isRequired,
  viewers: PropTypes.array.isRequired,
};

/**
 * "adding a note · 5m", composed through a translated key rather than concatenated,
 * so a locale can reorder the halves or punctuate between them differently.
 */
function describeViewer(t, viewer) {
  const elapsed = elapsedLabel(t, viewer.arrivedAt);
  const activity = activityLabel(t, viewer);

  return elapsed ? t('{{activity}} · {{elapsed}}', { activity, elapsed }) : activity;
}

/** Kept separate from the duration so the phrase list doesn't multiply by it. */
function activityLabel(t, { activity, activityTarget }) {
  if (activity !== CustomerViewerActivity.COMPOSING) {
    return t('viewing');
  }

  switch (activityTarget) {
    case CustomerViewerActivityTarget.NOTE:
      return t('adding a note');
    case CustomerViewerActivityTarget.TASK:
      return t('adding a task');
    case CustomerViewerActivityTarget.REPLY:
      return t('writing a reply');
    default:
      // A target this build doesn't know about — better to say they're working than
      // to claim they're only looking.
      return t('composing');
  }
}

// Derived from the arrival timestamp on a local tick rather than read off the
// heartbeats, which prove someone is still here but not how long they have been.
function elapsedLabel(t, arrivedAt) {
  if (!arrivedAt) {
    return null;
  }

  const minutes = Math.floor((Date.now() - new Date(arrivedAt).getTime()) / 60000);
  // An unparseable timestamp drops the duration rather than claiming "just now",
  // which would present a malformed record as the freshest arrival on screen.
  if (!Number.isFinite(minutes)) {
    return null;
  }
  if (minutes < 1) {
    return t('just now');
  }
  if (minutes < 60) {
    return t('{{count}}m', { count: minutes });
  }

  return t('{{count}}h', { count: Math.floor(minutes / 60) });
}

function useElapsedTick(isActive) {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!isActive) {
      return undefined;
    }

    const intervalId = setInterval(() => setTick(tick => tick + 1), TICK_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [isActive]);
}

// Tooltip measures its hover target to place the popover, and a bare span measures
// from the line's font metrics rather than the pill it contains — which puts a `top`
// popover over the pill instead of above it. A flex box gives it the pill's own rect,
// and the pill's border-radius keeps the focus ring tracing the pill.
const FocusTarget = styled.div`
  border-radius: 15px;
  display: inline-flex;
  max-width: 100%;
`;

const ViewersTooltip = styled(Tooltip)`
  display: inline-flex;
  max-width: 100%;

  ${Hoverer} {
    display: inline-flex;
    max-width: 100%;
  }
`;

// Animated on a pseudo-element's opacity rather than the pill's own box-shadow,
// which would repaint the pill and its text every frame.
const livePulse = keyframes`
  0%,
  100% {
    opacity: 0.15;
  }
  50% {
    opacity: 0.6;
  }
`;

// inline-flex, not flex: a block-level flex container would stretch to the full
// width of the profile column, and this should hug its contents.
const Pill = styled.div`
  align-items: center;
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.gray400};
  border-radius: 15px;
  color: ${p => p.theme.colors.gray800};
  cursor: default;
  display: inline-flex;
  font-size: ${p => p.theme.fontSize.base};
  gap: 6px;
  max-width: 100%;
  /* Left/vertical padding leaves room for the avatars' liveness ring. */
  padding: 4px 10px 4px 4px;
  position: relative;

  /* Offset by the 1px border so the glow starts at the pill's outer edge. The blur
   * reaches ~7px, inside the profile column's 16px gutter and the slot's 8px
   * padding, so it never clips against CustomerProfile's overflow. */
  &::after {
    animation: ${livePulse} 2.5s ease-in-out infinite;
    border-radius: inherit;
    bottom: -1px;
    box-shadow: 0 0 6px 1px ${p => p.theme.colors.yellow400};
    content: '';
    left: -1px;
    pointer-events: none;
    position: absolute;
    right: -1px;
    top: -1px;
  }

  /* Hold the cue at a steady mid-strength rather than removing it entirely. */
  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
      opacity: 0.5;
    }
  }
`;

const Avatars = styled.div`
  align-items: center;
  display: flex;
`;

// Flex rather than a plain block: .profileAvatar-iconWrapper-small is
// display:inline-block, so in a block parent it sits on a text baseline and picks up
// the line box's descender space, which pushes the circle off-centre.
const AvatarSlot = styled.div`
  align-items: center;
  display: flex;

  &:not(:first-child) {
    margin-left: -6px;
  }

  /* box-shadow rather than a border so the ring sits outside the circle without
   * shrinking the image; the outer white ring keeps overlapping avatars legible as
   * separate faces. */
  .profileAvatar-iconWrapper-small {
    box-shadow: 0 0 0 2px ${p => p.theme.colors.yellow400}, 0 0 0 3px ${p => p.theme.colors.white};
  }

  /* The small wrapper sizes .avatar and .profileAvatar-initialIcon-small but not the
   * unknown-profile glyph, which then draws at its natural size and spills over the
   * pill. Reachable whenever the agents provider hasn't loaded a name yet. */
  .profileAvatar-unknownCustomer {
    height: 24px;
    width: 24px;
  }
`;

const Label = styled.div`
  font-weight: ${p => p.theme.fontWeight.medium};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Elapsed = styled.div`
  color: ${p => p.theme.colors.gray600};
  white-space: nowrap;
`;

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ListRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;

  /* No white separator: rows don't overlap, and it would read as a gap on the
   * tooltip's own background. */
  .profileAvatar-iconWrapper-small {
    box-shadow: 0 0 0 2px ${p => p.theme.colors.yellow400};
  }
`;

const ListName = styled.div`
  font-weight: ${p => p.theme.fontWeight.medium};
`;

const ListElapsed = styled.div`
  opacity: 0.8;
  white-space: nowrap;
`;

export default CustomerViewersPill;
